/**
 * CaptureCategories — menu do "Capturar Leads" (propostas "Search", 17/10/2026, e "Busca",
 * 18/10/2026). Abre abaixo do botão do cabeçalho, sem escurecer a tela: um item por módulo
 * (ícone neutro, título, descrição e seta). A janela abre no último modo usado (janela ou tela
 * cheia; lib/capturePreference.ts). O teclado continua valendo (↑/↓ escolhem, Enter abre,
 * Esc fecha), mas a linha que explicava os atalhos saiu (18/10/2026); clicar fora fecha.
 * O Hunter saiu do painel em 17/10/2026 e não aparece.
 *
 * Movimento (23/10/2026): menu do guia (.ds-menu), sai do botão (canto de cima à direita) em
 * 140 ms, de 0,97 a 1, e fecha em 80 ms só com fade (volta do meio). Os itens trocam de fundo sem
 * transição, para não atrasar as setas. Para a saída aparecer, o cabeçalho deixa o menu sempre
 * montado e passa `aberto`: o menu fica no ar durante a saída (usePresenca) e, fechando, não
 * recebe clique nem tecla. Sem a prop, ele abre ao montar e some quando desmontado.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePresenca } from '../../hooks/usePresenca';
import { useSessionStore } from '../../stores/sessionStore';
import { VISIBLE_LIST_MODULES, type ListModule } from '../../config/constants';
import { CAPTURE_PRESENTATION_BY_MODULE } from '../../config/capturePresentation';
import { splitMenuLabel, useCaptureText } from './captureText';
import './searchWindow.css';
import { captureTypes } from '../../lib/entitlements';

interface CaptureCategoriesProps {
  /** Menu aberto (o menu cuida da entrada e da saída). Sem a prop, abre ao montar. */
  aberto?: boolean;
  onClose: () => void;
  onPick: (module: ListModule) => void;
}

/** Ordem do legado, sem o Hunter: places, phones, emails. */
const CATEGORY_ORDER = VISIBLE_LIST_MODULES;

export function CaptureCategories({
  aberto = true,
  onClose,
  onPick,
}: CaptureCategoriesProps): JSX.Element | null {
  const { t } = useCaptureText();
  const session = useSessionStore((s) => s.session);
  const [active, setActive] = useState(0);
  const menuRef = useRef<HTMLDivElement | null>(null);
  // Entra um quadro depois de montar (140 ms) e fica no ar enquanto sai (80 ms).
  const presenca = usePresenca(aberto, '--duracao-80');
  // Reaberto sem desmontar: começa do primeiro item, como ao montar.
  const [abertoAntes, setAbertoAntes] = useState(aberto);
  if (aberto !== abertoAntes) {
    setAbertoAntes(aberto);
    if (aberto) setActive(0);
  }

  const types = captureTypes(session);
  const available = CATEGORY_ORDER.filter(
    (mod) => types.includes(mod) && Boolean(CAPTURE_PRESENTATION_BY_MODULE[mod]),
  );

  useEffect(() => {
    // Fechando (ou fechado), o menu já não responde.
    if (!aberto) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (available.length === 0) return;
        const delta = e.key === 'ArrowDown' ? 1 : -1;
        setActive((i) => (i + delta + available.length) % available.length);
      } else if (e.key === 'Enter') {
        const mod = available[active];
        if (!mod) return;
        e.preventDefault();
        onPick(mod);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [aberto, available, active, onClose, onPick]);

  // O menu fica invisível (e sem foco possível) até ganhar .is-aberto: o foco vai depois.
  useEffect(() => {
    if (!presenca.aberto) return;
    const items = menuRef.current?.querySelectorAll<HTMLButtonElement>('button.cx-item');
    items?.[active]?.focus({ preventScroll: true });
  }, [active, presenca.aberto]);

  if (!presenca.montado) return null;
  return createPortal(
    <div
      className={`cx-overlay${aberto ? '' : ' is-saindo'}`}
      role="presentation"
      onClick={aberto ? onClose : undefined}
    >
      <div
        className={`cx-menu ds-menu${presenca.aberto ? ' is-aberto' : ''}`}
        role="menu"
        ref={menuRef}
        onClick={(e) => e.stopPropagation()}
      >
        {available.map((mod, index) => {
          const presentation = CAPTURE_PRESENTATION_BY_MODULE[mod]!;
          const { title, description } = splitMenuLabel(String(t(`module_${mod}_menu`, mod)));
          return (
            <button
              key={mod}
              type="button"
              className={`cx-item${index === active ? ' is-active' : ''}`}
              onMouseEnter={() => setActive(index)}
              onClick={() => onPick(mod)}
            >
              <span className="cx-item__icon material-symbols-rounded" aria-hidden="true">
                {presentation.icon}
              </span>
              <span className="cx-item__copy">
                <strong>{title}</strong>
                {description ? <span>{description}</span> : null}
              </span>
              <span className="cx-item__arrow material-symbols-rounded" aria-hidden="true">
                chevron_right
              </span>
            </button>
          );
        })}
      </div>
    </div>,
    document.body,
  );
}
